/**
 * Advanced → Spam protection — the bot check in front of this application's
 * public doors.
 *
 * The screen exists to make three things obvious, in this order:
 *
 *  1. WHAT IS IN FORCE RIGHT NOW. Most projects have configured nothing, and
 *     the honest answer is either "the platform's key" or "nothing at all" —
 *     not a blank form that implies neither.
 *  2. WHY A PROJECT WOULD CONFIGURE ITS OWN. A reCAPTCHA key is bound to a
 *     domain list in Google's console. An application served at a hostname its
 *     own operator owns cannot be covered by this platform's key, and that is
 *     not something to discover from flagged enquiries.
 *  3. THAT A LOW SCORE IS A JUDGEMENT, NOT A FACT. reCAPTCHA v3 returns a
 *     number, and what it means is the author's decision. Flagging is the
 *     default because a threshold nobody has tuned must not eat real
 *     enquiries; rejecting is offered plainly, next to the number.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertTriangle, Check, ExternalLink, Loader2, Shield, ShieldOff, Trash2,
} from 'lucide-react'
import {
  deleteProjectCaptcha, getProjectCaptcha, saveProjectCaptcha, testProjectCaptcha,
  type CaptchaSurface, type LowScorePolicy, type ProjectCaptcha,
} from '@/api/captcha'
import { invalidateCaptchaConfig } from '@/api/siteCaptcha'

const SURFACES: { key: CaptchaSurface; title: string; body: string }[] = [
  {
    key: 'forms',
    title: 'Forms on a public page',
    body: 'Contact forms, newsletter sign-ups and any form block a visitor can '
      + 'reach without signing in. This is the only door where a suspect '
      + 'submission can be kept and marked instead of refused — the inbox is '
      + 'somewhere to put it.',
  },
  {
    key: 'auth',
    title: 'Sign-up and password reset',
    body: 'Self-registration mints a real account, and every account counts as '
      + 'a runtime seat. A reset request e-mails a real person. Both are '
      + 'refused outright when the check fails.',
  },
  {
    key: 'transactions',
    title: 'Saves from an embedded transaction',
    body: 'An embedded transaction in add or edit mode on a public page is a '
      + 'door into this application’s own tables that anyone may post to. '
      + 'Reads are never checked — only the writes.',
  },
  {
    key: 'uploads',
    title: 'File uploads from a public page',
    body: 'Anonymous uploads into the document store. The download beside them '
      + 'is never checked: an image tag cannot run a script.',
  },
]

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

export default function SpamProtectionPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()

  const [cfg, setCfg] = useState<ProjectCaptcha | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  // Form state. `secret` starts empty and stays empty on a reload: the screen
  // is never sent a usable secret, so an empty box means "leave it alone", not
  // "clear it".
  const [siteKey, setSiteKey] = useState('')
  const [secret, setSecret] = useState('')
  const [minScore, setMinScore] = useState(0.5)
  const [onLowScore, setOnLowScore] = useState<LowScorePolicy>('flag')
  const [protect, setProtect] = useState<Record<CaptchaSurface, boolean>>({
    forms: true, auth: true, transactions: true, uploads: true,
  })

  const apply = useCallback((c: ProjectCaptcha) => {
    setCfg(c)
    setSiteKey(c.site_key || '')
    setSecret('')
    setMinScore(c.min_score)
    setOnLowScore(c.on_low_score)
    setProtect(c.protect)
  }, [])

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      apply(await getProjectCaptcha(project))
    } catch (err) {
      toast.error(errText(err, 'Could not load this application’s spam protection'))
    } finally {
      setLoading(false)
    }
  }, [project, apply])

  useEffect(() => { void load() }, [load])
  useEffect(() => { document.title = `Spam protection · ${project}` }, [project])

  const save = async (extra: Partial<{ enabled: boolean }> = {}) => {
    setSaving(true)
    try {
      const next = await saveProjectCaptcha(project, {
        site_key: siteKey.trim(),
        ...(secret ? { secret_key: secret } : {}),
        min_score: minScore,
        on_low_score: onLowScore,
        protect_forms: protect.forms,
        protect_auth: protect.auth,
        protect_transactions: protect.transactions,
        protect_uploads: protect.uploads,
        ...extra,
      })
      apply(next)
      // The published site key just changed; anything holding the old answer
      // would execute with a key this project no longer uses.
      invalidateCaptchaConfig()
      toast.success('Saved')
      return next
    } catch (err) {
      toast.error(errText(err, 'Could not save the spam protection settings'))
      return null
    } finally {
      setSaving(false)
    }
  }

  const onTest = async () => {
    // Test what is STORED, so a key typed but not saved cannot report a pass
    // the application will not get.
    const saved = secret || siteKey !== (cfg?.site_key || '') ? await save() : cfg
    if (!saved) return
    setTesting(true)
    try {
      const result = await testProjectCaptcha(project)
      if (result.ok) {
        toast.success('Google accepted the secret key')
        await load()
      } else {
        toast.error(result.error || 'Google did not accept these keys')
        await load()
      }
    } catch (err) {
      toast.error(errText(err, 'Could not check the keys'))
    } finally {
      setTesting(false)
    }
  }

  const onToggleEnabled = async () => {
    if (!cfg) return
    if (cfg.enabled) {
      if (!window.confirm(
        'Turn the bot check off for this application?\n\n'
        + 'Its public forms, sign-up, embedded transaction saves and file '
        + 'uploads go back to the honeypot and timing guards alone. This '
        + 'project will NOT fall back to the platform key — turning it off is '
        + 'a deliberate off.',
      )) return
    }
    await save({ enabled: !cfg.enabled })
  }

  const onRemove = async () => {
    if (!window.confirm(
      'Remove this application’s own keys?\n\n'
      + 'It goes back to whatever the platform provides — which may be nothing. '
      + 'To keep the keys but stop checking, turn the check off instead.',
    )) return
    try {
      apply(await deleteProjectCaptcha(project))
      invalidateCaptchaConfig()
      toast.success('Keys removed')
    } catch (err) {
      toast.error(errText(err, 'Could not remove the keys'))
    }
  }

  const inherited = cfg?.inherited || null
  const running = cfg?.enabled || (!cfg?.configured && !!inherited?.usable)

  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={18} className="spin" />
        <span style={{ color: 'var(--color-text-secondary)' }}>Loading…</span>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 940 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>
          &larr; Back
        </button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>
          Spam protection: {project}
        </h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px 44px' }}>
        Anything a visitor can put into this application without signing in —
        a contact form, a sign-up, a save from an embedded transaction, a file
        upload — is a door open to the whole internet. Google reCAPTCHA v3
        scores each attempt in the background, with nothing for a real visitor
        to solve.
      </p>

      {/* ── What is in force ── */}
      <div
        className="card"
        style={{
          padding: 16, marginBottom: 18, display: 'flex', gap: 12,
          borderLeft: `3px solid ${running
            ? 'var(--color-success, #16a34a)'
            : 'var(--color-warning, #d97706)'}`,
        }}
      >
        {running
          ? <Shield size={18} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
          : <ShieldOff size={18} style={{ color: 'var(--color-warning, #d97706)', flexShrink: 0 }} />}
        <div style={{ fontSize: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {cfg?.enabled
              ? 'Running on this application’s own key'
              : inherited?.usable
                ? 'Running on the platform’s key'
                : 'Not running'}
          </div>
          <div style={{ color: 'var(--color-text-secondary)' }}>
            {cfg?.enabled ? (
              <>
                Public entry points are checked against the key below.
                {cfg.verified_at
                  ? ' Google has accepted this secret.'
                  : ' Nobody has checked the secret yet — use Test keys.'}
              </>
            ) : inherited?.usable ? (
              <>
                This instance provides a key, so nothing is required here. It
                covers this platform&apos;s own hostnames only — if this
                application is served at a domain you own, configure its own key
                below or the check will fail for every visitor at that address.
              </>
            ) : (
              <>
                Public forms are guarded only by the hidden-field and timing
                probes, which a script written for this form defeats. Add a key
                pair below.
              </>
            )}
          </div>
          {inherited && !inherited.usable && (
            <div style={{ color: 'var(--color-warning, #d97706)', marginTop: 6 }}>
              The platform has a site key but no secret, so its check would run
              in the browser and never be verified. It is treated as off.
            </div>
          )}
        </div>
      </div>

      {cfg?.last_error && (
        <div className="card" style={{
          padding: 14, marginBottom: 18, display: 'flex', gap: 10,
          borderLeft: '3px solid var(--color-error, #dc2626)',
        }}>
          <AlertTriangle size={18} style={{ color: 'var(--color-error, #dc2626)', flexShrink: 0 }} />
          <div style={{ fontSize: 14 }}>{cfg.last_error}</div>
        </div>
      )}

      {/* ── Keys ── */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          This application&apos;s own keys
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          Create a <strong>reCAPTCHA v3</strong> key pair in the{' '}
          <a href="https://www.google.com/recaptcha/admin/create" target="_blank"
             rel="noreferrer noopener"
             style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            Google reCAPTCHA console <ExternalLink size={11} />
          </a>
          , listing every hostname this application is served at. The site key is
          published in the page; the secret never leaves this server.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label className="form-label" htmlFor="cap-site">Site key</label>
            <input
              id="cap-site" className="form-input" value={siteKey}
              onChange={(e) => setSiteKey(e.target.value)}
              placeholder="6Lc…" autoComplete="off" spellCheck={false}
            />
          </div>
          <div>
            <label className="form-label" htmlFor="cap-secret">Secret key</label>
            <input
              id="cap-secret" className="form-input" type="password" value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={cfg?.has_secret ? cfg.secret_masked : '6Lc…'}
              autoComplete="new-password" spellCheck={false}
            />
            <small style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {cfg?.has_secret
                ? 'Leave blank to keep the stored secret.'
                : 'Needed before the check can be turned on.'}
            </small>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" disabled={saving}
                  onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-sm" disabled={testing || saving || !siteKey}
                  onClick={() => void onTest()}>
            {testing ? 'Checking…' : 'Test keys'}
          </button>
          <button
            className="btn btn-sm"
            disabled={saving || !cfg?.configured
              || (!cfg?.enabled && !(cfg?.site_key && cfg?.has_secret))}
            onClick={() => void onToggleEnabled()}
            title={!cfg?.enabled && !(cfg?.site_key && cfg?.has_secret)
              ? 'Both keys are needed first'
              : undefined}
          >
            {cfg?.enabled ? 'Turn off' : 'Turn on'}
          </button>
          {cfg?.configured && (
            <button className="btn btn-ghost btn-sm" onClick={() => void onRemove()}
                    style={{ marginLeft: 'auto', color: 'var(--color-error, #dc2626)' }}>
              <Trash2 size={14} /> Remove keys
            </button>
          )}
        </div>
      </div>

      {/* ── Policy ── */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          What a low score does
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          reCAPTCHA v3 answers with a score from 0.0 (almost certainly a bot) to
          1.0 (almost certainly a person) — not a pass or a fail. A missing or
          forged token always blocks where blocking is possible; this setting is
          only about a <em>genuine</em> token that scored low.
        </p>

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 190px' }}>
            <label className="form-label" htmlFor="cap-score">
              Suspect below {minScore.toFixed(1)}
            </label>
            <input
              id="cap-score" type="range" min={0} max={1} step={0.1} value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <small style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              0.5 is Google&apos;s suggested starting point.
            </small>
          </div>
          <div style={{ flex: '1 1 260px' }}>
            <label className="form-label" htmlFor="cap-policy">Then</label>
            <select
              id="cap-policy" className="form-input" value={onLowScore}
              onChange={(e) => setOnLowScore(e.target.value as LowScorePolicy)}
            >
              <option value="flag">
                Keep it and mark it (forms); let everything else through
              </option>
              <option value="reject">Refuse it everywhere</option>
            </select>
            <small style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {onLowScore === 'flag'
                ? 'Nothing is lost: a form submission lands in the inbox marked '
                  + '“captcha_low_score”, and a wrong threshold is recoverable.'
                : 'A real customer whose browser scores badly — a strict privacy '
                  + 'setting, a shared office address — is turned away with no trace. '
                  + 'Watch the scores in the form inbox before choosing this.'}
            </small>
          </div>
        </div>
      </div>

      {/* ── Which doors ── */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          Which doors are checked
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          All of them, unless something here already proves a person. A
          signed-in visitor is never checked on any of them.
        </p>
        {SURFACES.map((s) => (
          <label
            key={s.key}
            style={{
              display: 'flex', gap: 10, padding: '10px 0', alignItems: 'flex-start',
              borderTop: '1px solid var(--color-border)', cursor: 'pointer',
            }}
          >
            <input
              type="checkbox" checked={protect[s.key]} style={{ marginTop: 3 }}
              onChange={(e) => setProtect((p) => ({ ...p, [s.key]: e.target.checked }))}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{s.body}</div>
            </div>
          </label>
        ))}
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary btn-sm" disabled={saving}
                  onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {cfg?.verified_at && (
            <span style={{
              marginLeft: 12, fontSize: 13, color: 'var(--color-success, #16a34a)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <Check size={13} /> Secret verified{' '}
              {new Date(cfg.verified_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
